import React from "react";

import Stack from "@mui/material/Stack";

import EnumSelectorInput from "../../common/inputs/EnumSelectorInput";
import ToggleField from "../../common/inputs/ToggleField";
import UnitField from "../../common/inputs/UnitField";
import ValueReset from "../../common/inputs/ValueReset";
import { fieldAccess } from "../../parametersProcessor/paramAccess";
import { fieldDefault } from "../../parametersProcessor/paramDefaults";
import { ParametersContext } from "../../parametersProcessor/ParametersProcessor";
import { isParamVisible } from "../../parametersProcessor/paramVisibility";
import {
    IBackPassingParamTypeDescriptor,
    IBackPassingSizeField,
    IRelationFieldOverride,
} from '../../database/interfaces/paramScript/IParamTypeDescriptor';
import { localizedOptions } from "./enumOptions";

/**
 * BackPassing (Kötőelemek) composite card: the CabinetFrame BackBoard relation and its parameters.
 * The value is an object {Relation, Depth, Margin, Top} — all string-valued, exactly as Unity expects
 * (e.g. {"Relation":"Groove","Depth":"10mm","Margin":"5mm","Top":"true"}). Mirrors the Decor card's
 * copy-mutate-emit pattern: every change emits the WHOLE object.
 *
 * Field mapping to the Unity Relations Back entry (labels after V38: depth="Ráhagyás", margin="Mélység"):
 *   - Depth  ("Ráhagyás") -> TargetMargin, the edge margin — shown for ALL relations (Butt/Inlay/Groove)
 *   - Margin ("Mélység")  -> TargetDepth,  the groove depth — shown for Groove ONLY
 *   - Top                 -> Top (boolean) — always shown, emitted on every Back entry
 *
 * A méret-mezők defaultja KÖTÉS-FÜGGŐ lehet (descriptor `depth.defaultByRelation` / `margin.
 * defaultByRelation`): ráfekvő (Butt) kötésnél a Ráhagyás 0, mert ott nincs mit ráhagyni. Kötés-váltás
 * a mezőket az új kötés defaultjára állítja — az értékek az ADATBÓL jönnek, nem a kódból.
 */
interface IBackPassingCardContentProps {
    typeDescriptor: IBackPassingParamTypeDescriptor;
    paramValue: any;
    /**
     * A paraméter GLOBÁLIS (nem felhasználó-specifikus) alapértéke — ugyanolyan alakú objektum, mint az
     * érték. A kártya MEZŐNKÉNT rak ki "visszaállítás alapértelmezettre" gombot (kötés, ráhagyás,
     * mélység), ezért kell neki a mezőnkénti alapérték is. Hiányában egyszerűen nincs gomb.
     */
    storedDefault?: any;
    onUpdateParamValue: (value: any) => void;
}

const BackPassingCardContent: React.FC<IBackPassingCardContentProps> = ({ typeDescriptor, paramValue, storedDefault, onUpdateParamValue }) => {
    const d = typeDescriptor;
    // paramValue is the decoded object; may be "" before the value has loaded — fall back to defaults.
    const cur = (paramValue && typeof paramValue === "object") ? paramValue : {};

    // A kötés-típus és MINDEN default a TYPE DESCRIPTORBÓL jön — a kódban nincs se kötés-név, se
    // méret-érték. Ha a descriptor nem ad `default`-ot, az első felsorolt opció az alapértelmezett
    // (szintén adat), nem egy kódba írt kötés.
    const relation: string = cur.Relation
        ?? d.relation?.default
        ?? Object.keys(d.relation?.options ?? {})[0]
        ?? "";

    type TSubField = IBackPassingSizeField | undefined;

    /**
     * A mező KÖTÉS-FÜGGŐ felülírásai a descriptorból (`byRelation[kötés]`): érték és/vagy numerikus
     * korlátok. Ha a mező nem deklarál `byRelation`-t (és legacy `defaultByRelation`-t sem), akkor nem
     * kötés-függő. A kódban nincs sem kötés-név, sem méret — mindkettő adat.
     */
    const relationOverride = (field: TSubField, rel: string): IRelationFieldOverride | undefined =>
        field?.byRelation?.[rel];

    /**
     * A kötéshez tartozó alapértelmezett ÉRTÉK, ha a mező kötés-függő; egyébként undefined (ilyenkor
     * kötés-váltáskor nem nyúlunk a mezőhöz, marad a felhasználó értéke). A legacy `defaultByRelation`
     * alakot is olvassuk, hogy egy még nem migrált descriptor viselkedése se változzon.
     */
    const relationDefault = (field: TSubField, rel: string): string | undefined => {
        if (field?.byRelation) return relationOverride(field, rel)?.default ?? field.default;
        if (field?.defaultByRelation) return field.defaultByRelation[rel] ?? field.default;
        return undefined;
    };

    /**
     * Kezdőérték, ha még nincs eltárolt érték: a kötés-függő default, egyébként a mező `default`-ja.
     * Ha a descriptor egyiket sem adja meg, üres stringet adunk vissza — a UnitField ilyenkor a
     * mező `units` configjából veszi a számot és az egységet (`cfg.default`, első egység-név), tehát a
     * végső fallback is ADAT, nem kódba írt méret.
     */
    const initialValue = (field: TSubField, rel: string): string =>
        relationDefault(field, rel) ?? field?.default ?? "";

    const depthStr: string = cur.Depth ?? initialValue(d.depth, relation);
    const marginStr: string = cur.Margin ?? initialValue(d.margin, relation);
    const channelWidthStr: string = cur.ChannelWidth ?? initialValue(d.channelWidth, relation);
    // A Top kezdőállapota is a descriptorból (`top.default`); ha az sem adja meg, a String()-es
    // összehasonlítás magától false-ot ad — nincs kódba írt üzleti default.
    const topBool: boolean = String(cur.Top ?? d.top?.default) === "true";
    // Melyik oldal látszik a kész bútoron (aszimmetrikus hátlap). Csak akkor létező fogalom, ha a
    // descriptor deklarálja — enélkül a kártya és a tárolt érték a mai négy mezős marad.
    const visibleSide: string = cur.VisibleSide ?? d.visibleSide?.default ?? "";

    const access = fieldAccess(d);

    // Always emit the full object so Unity gets a complete descriptor. Top is a "true"/"false" string.
    const emit = (patch: { Relation?: string; Depth?: string; Margin?: string; ChannelWidth?: string; Top?: boolean; VisibleSide?: string }) => {
        onUpdateParamValue({
            Relation: patch.Relation ?? relation,
            Depth: patch.Depth ?? depthStr,
            Margin: patch.Margin ?? marginStr,
            ...(d.channelWidth ? { ChannelWidth: patch.ChannelWidth ?? channelWidthStr } : {}),
            Top: (patch.Top ?? topBool) ? "true" : "false",
            ...(d.visibleSide ? { VisibleSide: patch.VisibleSide ?? visibleSide } : {}),
        });
    };

    // Melyik mező jelenik meg az adott kötésnél: a descriptor `visibleForRelations`-je mondja meg
    // (kulcs nélkül a mező mindig látszik) — nincs kötés-névre írt feltétel a kódban. Ugyanaz a minta,
    // mint a railing kártyán a `railingWidth.visibleForTypes`. MINDHÁROM mezőre érvényes (Ráhagyás,
    // Mélység, Felső él), így bármelyik kötés-függővé tehető adatból, kód-módosítás nélkül. Jelenlegi
    // adat: a Ráhagyás és a Felső él minden kötésnél látszik, a nút-mélység csak nútolásnál.
    const isVisibleForRelation = (field: { visibleForRelations?: string[] } | undefined): boolean =>
        field?.visibleForRelations?.includes(relation) ?? true;

    const showDepth = isVisibleForRelation(d.depth);
    const showMargin = isVisibleForRelation(d.margin);
    const showChannelWidth = !!d.channelWidth && isVisibleForRelation(d.channelWidth);
    // A `top.offWhen` (pl. bekapcsolt merevítő) a kimenetben kikapcsolja a Topot — itt a kapcsoló sem látszik.
    const { parameters } = React.useContext(ParametersContext);
    const showTop = isVisibleForRelation(d.top) && !(d.top?.offWhen && isParamVisible(parameters, d.top.offWhen));
    const showVisibleSide = !!d.visibleSide && isVisibleForRelation(d.visibleSide);

    // "Visszaállítás alapértelmezettre" MEZŐNKÉNT (a kötés-kártya composite, egy kártya-szintű gomb
    // nem tudná, MELYIK mezőt akarta a felhasználó visszaállítani). Zárolt kártyán nincs gomb.
    /**
     * Egy méret-mező alapértéke. A forrás-sorrend a globális szabályt követi (lásd paramDefaults):
     * globális alapérték → kötés-függő descriptor-default → a mező `units` alapértéke. A KÖTÉS-FÜGGŐ
     * korlátokat (`byRelation[kötés]`) ugyanúgy átadjuk, ahogy a beviteli mezőnek is — így egy másik
     * kötésnél érvénytelenné vált gyári érték a kötéshez tartozó korlátok közé kerül.
     */
    const sizeFieldDefault = (field: TSubField, storedValue: any) =>
        access.resettable
            ? fieldDefault(
                { units: field?.units, override: relationOverride(field, relation) },
                storedValue ?? relationDefault(field, relation)
            )
            : null;

    return d ? (
        <Stack direction="column" spacing={1.5}>
            <ValueReset
                paramDefault={access.resettable
                    ? fieldDefault(
                        { options: d.relation?.options, default: d.relation?.default },
                        storedDefault?.Relation
                    )
                    : null}
                currentValue={relation}
                // A visszaállítás UGYANAZT az utat járja, mint a kézi választás (a kötés-függő mezők is
                // követik az új kötést) — nincs külön írási ág.
                onReset={(v) => emit({
                    Relation: v,
                    Depth: relationDefault(d.depth, v),
                    Margin: relationDefault(d.margin, v),
                })}
            >
                <EnumSelectorInput
                    label={d.relation?.label}
                    value={relation}
                    options={d.relation?.options || {}}
                    tooltip={d.tooltip}
                    editable={access.editable}
                    readOnly={access.readOnly}
                    // Kötés-váltáskor CSAK a kötés-függő mezők (amiknek van `defaultByRelation`-juk) állnak
                    // az új kötés defaultjára — ráfekvőnél a Ráhagyás 0-ra. A többi mező (pl. a nútolás
                    // Mélysége) érintetlen marad, hogy egy oda-vissza váltás ne mossa el a user értékét.
                    onValueChange={(v) => emit({
                        Relation: v,
                        Depth: relationDefault(d.depth, v),
                        Margin: relationDefault(d.margin, v),
                    })}
                />
            </ValueReset>
            {showVisibleSide && (
                <ValueReset
                    paramDefault={access.resettable
                        ? fieldDefault(
                            { options: d.visibleSide?.options, default: d.visibleSide?.default },
                            storedDefault?.VisibleSide
                        )
                        : null}
                    currentValue={visibleSide}
                    onReset={(v) => emit({ VisibleSide: v })}
                >
                    <EnumSelectorInput
                        label={d.visibleSide?.label}
                        value={visibleSide}
                        options={localizedOptions("VisibleSide", d.visibleSide?.options || {})}
                        editable={access.editable}
                        readOnly={access.readOnly}
                        onValueChange={(v) => emit({ VisibleSide: v })}
                    />
                </ValueReset>
            )}
            {showDepth && (
                <ValueReset
                    paramDefault={sizeFieldDefault(d.depth, storedDefault?.Depth)}
                    currentValue={depthStr}
                    onReset={(v) => emit({ Depth: v })}
                >
                    <UnitField
                        label={d.depth?.label}
                        valueStr={depthStr}
                        units={d.depth?.units}
                        access={access}
                        override={relationOverride(d.depth, relation)}
                        unitChange="keep"
                        onChange={(v) => emit({ Depth: v })}
                    />
                </ValueReset>
            )}
            {showMargin && (
                <ValueReset
                    paramDefault={sizeFieldDefault(d.margin, storedDefault?.Margin)}
                    currentValue={marginStr}
                    onReset={(v) => emit({ Margin: v })}
                >
                    <UnitField
                        label={d.margin?.label}
                        valueStr={marginStr}
                        units={d.margin?.units}
                        access={access}
                        override={relationOverride(d.margin, relation)}
                        unitChange="keep"
                        onChange={(v) => emit({ Margin: v })}
                    />
                </ValueReset>
            )}
            {showChannelWidth && (
                <ValueReset
                    paramDefault={sizeFieldDefault(d.channelWidth, storedDefault?.ChannelWidth)}
                    currentValue={channelWidthStr}
                    onReset={(v) => emit({ ChannelWidth: v })}
                >
                    <UnitField
                        label={d.channelWidth?.label}
                        valueStr={channelWidthStr}
                        units={d.channelWidth?.units}
                        access={access}
                        override={relationOverride(d.channelWidth, relation)}
                        unitChange="keep"
                        onChange={(v) => emit({ ChannelWidth: v })}
                    />
                </ValueReset>
            )}
            {showTop && (
                <ToggleField
                    label={d.top?.label || "Top"}
                    checked={topBool}
                    access={access}
                    onChange={(checked) => emit({ Top: checked })}
                />
            )}
        </Stack>
    ) : null;
};

export default BackPassingCardContent;
